// src/hooks/useTicketDetail.ts
//
// The ticket-screen data hook (Story 4.2). Plain useState/useCallback/useEffect
// per the repo's hook style (useTickets/useRegions) — NO React Query. All reads
// go through ticketsService (AD-3); the composite detail bundle is assembled
// here from parallel fetches. The ONLY mutation is link() → tx_link_tickets.
//
// Live updates: TWO channels via the shared useRealtimeRefetch — yak_tickets
// (status/assignee/field changes; every transition updates the row in-txn) and
// yak_ticket_messages (thread inserts). A realtime refetch is SILENT (no loading
// flash, no focus stealing, no optimistic state — the 1.1/4.1 non-contradiction
// rule) and raises a transient "Updated just now" marker. Only the initial /
// explicit load owns the error banner.

import { useState, useEffect, useCallback, useRef } from 'react'
import { ticketsService } from '../services/ticketsService'
import { regionsService } from '../services/regionsService'
import { useRealtimeRefetch } from './useRealtimeRefetch'
import type { YakTicketDetail } from '../types/yak'

export interface UseTicketDetailReturn {
  detail: YakTicketDetail | null
  loading: boolean
  error: string | null
  updatedJustNow: boolean
  linking: boolean
  responding: boolean
  noting: boolean
  closing: boolean
  escalating: boolean
  reassigning: boolean
  // The field path currently being corrected (null = none) — keyed per field so
  // one save doesn't lock the whole structured-fields panel.
  correctingField: string | null
  refetch: () => Promise<void>
  link: (relatedTicketId: string) => Promise<void>
  respond: (body: string) => Promise<void>
  note: (body: string) => Promise<void>
  close: () => Promise<void>
  escalate: (note: string | null) => Promise<void>
  reassign: (targetUserId: string) => Promise<void>
  correctField: (field: string, value: string | null) => Promise<void>
}

const UPDATED_MARKER_MS = 4000

export function useTicketDetail(
  ticketId: string | undefined
): UseTicketDetailReturn {
  const [detail, setDetail] = useState<YakTicketDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [updatedJustNow, setUpdatedJustNow] = useState(false)
  const [linking, setLinking] = useState(false)
  const [responding, setResponding] = useState(false)
  const [noting, setNoting] = useState(false)
  const [closing, setClosing] = useState(false)
  const [escalating, setEscalating] = useState(false)
  const [reassigning, setReassigning] = useState(false)
  const [correctingField, setCorrectingField] = useState<string | null>(null)

  // Keep the current id in a ref so the stable realtime callback always refetches
  // the current ticket without re-subscribing.
  const idRef = useRef(ticketId)
  idRef.current = ticketId

  const markerTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetchDetail = useCallback(async (silent = false) => {
    const id = idRef.current
    if (!id) {
      setDetail(null)
      setLoading(false)
      return
    }
    try {
      if (!silent) {
        setLoading(true)
        setError(null)
      }
      const ticket = await ticketsService.getTicket(id)
      if (idRef.current !== id) return // id changed mid-flight — drop stale result
      if (!ticket) {
        setDetail(null)
        return
      }

      const [
        call,
        siblings,
        assignments,
        pins,
        messages,
        corrections,
        relatedTickets,
        rule,
        regions,
      ] = await Promise.all([
        ticketsService.getCall(ticket.call_id),
        ticketsService.listCallTickets(ticket.call_id),
        ticketsService.listAssignments(id),
        ticketsService.listKbPins(id),
        ticketsService.listMessages(id),
        ticketsService.listCorrections(id),
        ticketsService.listLinks(id),
        ticketsService.getRulesRowForType(ticket.question_type),
        regionsService.listRegions(),
      ])
      if (idRef.current !== id) return

      const rawRegionId = ticket.structured_fields?.['region_id']
      const regionId = typeof rawRegionId === 'string' ? rawRegionId : null
      const regionName = regionId
        ? (regions.find(r => r.id === regionId)?.name ?? null)
        : null

      setDetail({
        ticket,
        call,
        siblings,
        assignments,
        pins,
        messages,
        corrections,
        relatedTickets,
        regionName,
        regions,
        rule,
      })

      if (silent) {
        setUpdatedJustNow(true)
        if (markerTimer.current) clearTimeout(markerTimer.current)
        markerTimer.current = setTimeout(
          () => setUpdatedJustNow(false),
          UPDATED_MARKER_MS
        )
      }
    } catch (err) {
      if (!silent) {
        setError(err instanceof Error ? err.message : 'Failed to load ticket')
      } else {
        // Silent refresh failure: keep the last-known screen, only log (a failed
        // background refetch must not blank a page that is already valid).
        console.error('Ticket detail refresh failed', err)
      }
    } finally {
      if (!silent) setLoading(false)
    }
  }, [])

  // Visible (non-silent) load whenever the ticket id changes.
  useEffect(() => {
    fetchDetail()
  }, [ticketId, fetchDetail])

  // Clear any pending marker timer on unmount.
  useEffect(() => {
    return () => {
      if (markerTimer.current) clearTimeout(markerTimer.current)
    }
  }, [])

  const silentRefetch = useCallback(() => {
    fetchDetail(true)
  }, [fetchDetail])
  useRealtimeRefetch('yak_tickets', silentRefetch)
  useRealtimeRefetch('yak_ticket_messages', silentRefetch)

  // The one mutation. Errors propagate to the overlay (mapped by unwrapTx); the
  // finally clears the pending flag either way. No optimistic row — the screen
  // re-renders from the server's answer via the silent refetch.
  const link = useCallback(
    async (relatedTicketId: string) => {
      const id = idRef.current
      if (!id) return
      setLinking(true)
      try {
        await ticketsService.linkTickets(id, relatedTicketId)
        await fetchDetail(true)
      } finally {
        setLinking(false)
      }
    },
    [fetchDetail]
  )

  // Story 4.3 write actions. Each sets a per-action pending flag, calls the
  // service, and refetches on success — the screen re-renders from the SERVER's
  // answer (no optimistic lifecycle state; 4.2's realtime channels also carry
  // the message INSERT + ticket UPDATE to every open session). Mapped errors
  // rethrow for inline display; the finally clears the flag either way.
  const respond = useCallback(
    async (body: string) => {
      const id = idRef.current
      if (!id) return
      setResponding(true)
      try {
        await ticketsService.respond(id, body)
        await fetchDetail(true)
      } finally {
        setResponding(false)
      }
    },
    [fetchDetail]
  )

  const note = useCallback(
    async (body: string) => {
      const id = idRef.current
      if (!id) return
      setNoting(true)
      try {
        await ticketsService.note(id, body)
        await fetchDetail(true)
      } finally {
        setNoting(false)
      }
    },
    [fetchDetail]
  )

  const close = useCallback(async () => {
    const id = idRef.current
    if (!id) return
    setClosing(true)
    try {
      await ticketsService.close(id)
      await fetchDetail(true)
    } finally {
      setClosing(false)
    }
  }, [fetchDetail])

  // Story 4.4 write actions. Same idiom: per-action pending flag, refetch on
  // success (the screen re-renders from the SERVER's answer — no optimistic
  // assignee), mapped errors rethrow for inline display. When the actor escalates
  // away their own ticket, 4.1's My-tickets subscription drops it live for free.
  const escalate = useCallback(
    async (note: string | null) => {
      const id = idRef.current
      if (!id) return
      setEscalating(true)
      try {
        await ticketsService.escalate(id, note)
        await fetchDetail(true)
      } finally {
        setEscalating(false)
      }
    },
    [fetchDetail]
  )

  const reassign = useCallback(
    async (targetUserId: string) => {
      const id = idRef.current
      if (!id) return
      setReassigning(true)
      try {
        await ticketsService.reassign(id, targetUserId)
        await fetchDetail(true)
      } finally {
        setReassigning(false)
      }
    },
    [fetchDetail]
  )

  // Story 4.5: correct one structured field. Per-field pending (so a save on one
  // row doesn't lock the whole panel), refetch on success (the screen re-renders
  // from the SERVER's answer — no optimistic field state; the yak_tickets UPDATE
  // also reaches every open session via 4.2's realtime channel — Decision 10).
  // Mapped errors rethrow for inline display; the finally clears the flag.
  const correctField = useCallback(
    async (field: string, value: string | null) => {
      const id = idRef.current
      if (!id) return
      setCorrectingField(field)
      try {
        await ticketsService.correctField(id, field, value)
        await fetchDetail(true)
      } finally {
        setCorrectingField(null)
      }
    },
    [fetchDetail]
  )

  return {
    detail,
    loading,
    error,
    updatedJustNow,
    linking,
    responding,
    noting,
    closing,
    escalating,
    reassigning,
    correctingField,
    refetch: () => fetchDetail(),
    link,
    respond,
    note,
    close,
    escalate,
    reassign,
    correctField,
  }
}
